import { useState } from 'react';
import { Button, Flex, Popconfirm, theme, Tooltip } from 'antd';
import { IoEyeOffOutline, IoEyeOutline, IoTrashOutline } from 'react-icons/io5';

import BaseText from '@components/typography/BaseText';
import { capitalizeFirst, formatDate } from '@utils/helpers';

import { Line, Note, PanelRow } from './FindPanel';
import { useSavedFinds } from './useSavedFinds';

// More saved results than this fold away behind "Show all"
const FOLDED = 3;

// "Trees", "Car, house", or the name it was saved under
const jobTitle = job =>
	capitalizeFirst(job.name || (job.labels ?? []).filter(Boolean).join(', ') || 'Find results');

// "803 · 12. okt. 2026"
const jobNote = job =>
	[job.count ?? null, job.created_at ? formatDate(job.created_at) : null]
		.filter(part => part !== null)
		.join(' · ');

const SavedJob = ({ job, shown, loading, removing, onToggle, onRemove }) => {
	const title = jobTitle(job);
	return (
		<Line>
			<Flex vertical style={{ minWidth: 0, flex: 1 }}>
				<BaseText ellipsis>{title}</BaseText>
				<Note>{jobNote(job)}</Note>
			</Flex>
			<Flex gap="small">
				<Tooltip title={shown ? 'Hide on the map' : 'Show on the map'}>
					<Button
						type="text"
						size="small"
						icon={shown ? <IoEyeOutline /> : <IoEyeOffOutline />}
						loading={loading}
						aria-label={`${shown ? 'Hide' : 'Show'} ${title}`}
						aria-pressed={shown}
						onClick={onToggle}
					/>
				</Tooltip>
				<Popconfirm
					title="Delete these saved results?"
					description="The job and its boxes go from the project."
					okText="Delete"
					okButtonProps={{ danger: true }}
					cancelText="Cancel"
					onConfirm={onRemove}>
					<Button
						type="text"
						size="small"
						icon={<IoTrashOutline />}
						loading={removing}
						aria-label={`Delete ${title}`}
					/>
				</Popconfirm>
			</Flex>
		</Line>
	);
};

/**
 * Saved: the project's saved Find results, each to show on the map (dashed, in the accent colour)
 * or delete. Nothing while there are none.
 */
const SavedFinds = ({ mapObject, projectUuid, open }) => {
	const { token } = theme.useToken();
	const saved = useSavedFinds({ mapObject, projectUuid, open, token });
	const [unfolded, setUnfolded] = useState(false);
	const [failed, setFailed] = useState(null);
	const { jobs } = saved;
	if (jobs.length === 0) return null;
	const listed = unfolded ? jobs : jobs.slice(0, FOLDED);
	const remove = jobUuid => {
		setFailed(null);
		return saved
			.remove(jobUuid)
			.catch(() => setFailed('That could not be deleted just now. Try again in a moment.'));
	};

	return (
		<PanelRow label="Saved">
			{listed.map(job => (
				<SavedJob
					key={job.job_uuid}
					job={job}
					shown={saved.shown.has(job.job_uuid)}
					loading={saved.loading.has(job.job_uuid)}
					removing={saved.removing === job.job_uuid}
					onToggle={() => saved.toggle(job.job_uuid)}
					onRemove={() => remove(job.job_uuid)}
				/>
			))}
			{jobs.length > FOLDED && (
				<Flex>
					<Button
						type="link"
						size="small"
						onClick={() => setUnfolded(current => !current)}>
						{unfolded ? 'Show fewer' : `Show all ${jobs.length}`}
					</Button>
				</Flex>
			)}
			{failed && <Note role="alert">{failed}</Note>}
		</PanelRow>
	);
};

export default SavedFinds;
